import React, { memo, FC } from "react";
import {
  Typography,
  FormControl,
  FormGroup,
  Checkbox,
  FormLabel,
  FormControlLabel,
} from "@mui/material";
import Choices from "../Choices";
import ReusableAccordian from "../ReusableAccordion";
import PleaseNote from "../PleaseNote";
import { Image } from "cloudinary-react";
import SillFinish from "../SillFinish/SillFinish";
import {
  sillColor,
  sillImgs,
  raisedSills,
  sills,
  outswingSills,
  inswingSills,
} from "./defaults";
import { RowLayout, ColLayout } from "../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import useSillSelector from "./useSillSelector";
import { addScrollElements } from "../AccordionWrapper/AccordianWrapper";

const ColLayoutSpacing = styled("div")(({ theme }) => ({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(1, 2),
}));

const ColLayoutLeftDivider = styled("div")(({ theme }) => ({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(1, 2),
  borderLeft: `2px solid ${theme.palette.divider}`,
}));

const RowLayoutSpacing = styled("div")(({ theme }) => ({
  marginBottom: 10,
  alignItems: "center",
  justifyContent: "center",
  display: "flex",
  padding: theme.spacing(1, 2),
  alignSelf: "flex-end",
  marginTop: 20,
}));

const StyledFormControl = styled(FormControl)({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  justifyContent: "center",
  display: "flex",
});

const SillAccordian: FC = () => {
  const {
    handleClick,
    showSillFinish,
    showRaised,
    direction,
    sillStyle,
    sillColorChoice,
    doorType,
    screenVal,
    isCentor,
    isComboSillDifferent,
    comboWindowSillStyle,
    handleCheck,
    showRaisedComboWindow,
    handleClickCombo,
  } = useSillSelector();

  return (
    <>
      {addScrollElements([
        "Sill Finish",
        "Sill Dam Finish",
        "select either Rounded or Squared Sill",
        "select either Rounded or Squared Window Sill",
        "Sill Type",
        "Window Sill Type",
      ])}

      <ReusableAccordian
        accordionName="sill"
        heading="Sill Choice"
        secondaryHeading="Select sill choice"
        colOrRow="col"
      >
        {doorType === "Swing" && (
          <>
            <PleaseNote notes="Note: For ADA Sill Please Contact Office" />

            <RowLayout>
              <RowLayoutSpacing>
                {direction === "Inswing" && (
                  <Image
                    cloudName="ag-millworks"
                    secure="true"
                    publicId="Sills/swings/inswingSill"
                  />
                )}

                {direction === "Outswing" && screenVal && (
                  <Image
                    cloudName="ag-millworks"
                    secure="true"
                    publicId={`Sills/swings/outswing${
                      isCentor ? "Centor" : "Screen"
                    }`}
                  />
                )}

                {direction === "Outswing" && !screenVal && (
                  <Image
                    cloudName="ag-millworks"
                    secure="true"
                    publicId="Sills/swings/outswingSill"
                  />
                )}

                {direction === "Inswing" && (
                  <Choices
                    wide={true}
                    label={inswingSills.label}
                    variant="filled"
                    checkMark={true}
                    disabled={inswingSills.disabled || false}
                  />
                )}

                {direction === "Outswing" && (
                  <Choices
                    label={outswingSills.label}
                    variant="filled"
                    checkMark={true}
                    disabled={outswingSills.disabled || false}
                  />
                )}

                {!direction && <PleaseNote notes="Select Swing Direction" />}
              </RowLayoutSpacing>

              <SillColor
                handleClick={handleClick}
                sillColorChoice={sillColorChoice || ""}
              />

              {showSillFinish && <SillFinish />}
            </RowLayout>
          </>
        )}

        {doorType.includes("Bi-fold") && (
          <>
            <PleaseNote notes="Ultra Guide and ADA Ultra Guide are appropriate for extremely low-exposure environments only; no warranty for water penetration, wind Penetration, or airborne particles with this option" />

            <ColLayout>
              <RowLayout>
                <ColLayoutSpacing>
                  <Typography style={{ margin: "auto" }} variant="subtitle1">
                    Sill Types
                    <span style={{ color: "red", fontSize: 20 }}>*</span>
                  </Typography>
                  <RowLayout>
                    {sills.map((state) => (
                      <div
                        key={state.id}
                        onClick={() => handleClick(state.label, state.type)}
                      >
                        <Choices
                          label={state.label}
                          variant={
                            state.label === sillStyle ||
                            (state.label === "Raised Sill" &&
                              sillStyle?.includes("Dam"))
                              ? "filled"
                              : "outlined"
                          }
                          checkMark={
                            state.label === sillStyle ||
                            (state.label === "Raised Sill" &&
                              sillStyle?.includes("Dam"))
                              ? true
                              : false
                          }
                          disabled={state.disabled || false}
                        />
                      </div>
                    ))}
                  </RowLayout>

                  {showRaised && (
                    <RowLayout>
                      {screenVal && !isCentor ? (
                        <Choices
                          label={raisedSills[1].label}
                          variant={"filled"}
                          checkMark={true}
                          disabled={true}
                        />
                      ) : (
                        raisedSills.map((state) => (
                          <div
                            key={state.id}
                            onClick={() => handleClick(state.label, state.type)}
                          >
                            <Choices
                              label={state.label}
                              variant={
                                state.label === sillStyle
                                  ? "filled"
                                  : "outlined"
                              }
                              checkMark={state.label === sillStyle}
                              disabled={!screenVal ? false : state.disabled}
                            />
                          </div>
                        ))
                      )}
                    </RowLayout>
                  )}
                </ColLayoutSpacing>

                <SillColor
                  handleClick={handleClick}
                  sillColorChoice={sillColorChoice || ""}
                />

                {showSillFinish && <SillFinish />}
              </RowLayout>

              {sillStyle &&
                sillImgs
                  .filter((image) => image.label === sillStyle)
                  .map((imageShown) => (
                    <RowLayout key={imageShown.label}>
                      <Image
                        cloudName="ag-millworks"
                        secure="true"
                        publicId={
                          screenVal && !isCentor
                            ? imageShown.imgLabelScreen
                            : screenVal && isCentor
                              ? imageShown.imgLabelCentor
                              : imageShown.imgLabel
                        }
                      />

                      {doorType.includes("Bi-fold") &&
                        sillStyle?.includes("Ultra") && (
                          <div style={{ marginLeft: 20 }}>
                            <Image
                              cloudName="ag-millworks"
                              secure="true"
                              publicId="Sills/bifolds/ultra-guide-weep"
                            />
                          </div>
                        )}
                    </RowLayout>
                  ))}

              {doorType === "Bi-fold Door/Window Combo" && (
                <>
                  <StyledFormControl>
                    <FormLabel style={{ color: "black" }} component="legend">
                      Would you like the window sill type to match the door?
                      <span style={{ color: "red", fontSize: 22 }}>*</span>
                    </FormLabel>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={
                              isComboSillDifferent === null ||
                              isComboSillDifferent
                                ? false
                                : true
                            }
                            onChange={(e) => handleCheck(e, "yes")}
                          />
                        }
                        label="Yes"
                        labelPlacement="end"
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={
                              isComboSillDifferent === null ||
                              !isComboSillDifferent
                                ? false
                                : true
                            }
                            onChange={(e) => handleCheck(e, "no")}
                          />
                        }
                        label="No"
                        labelPlacement="end"
                      />
                    </FormGroup>
                  </StyledFormControl>

                  {isComboSillDifferent && (
                    <ColLayoutSpacing>
                      <Typography
                        style={{ margin: "auto" }}
                        variant="subtitle1"
                      >
                        Sill Types (Window)
                        <span style={{ color: "red", fontSize: 20 }}>*</span>
                      </Typography>
                      <RowLayout>
                        {sills
                          .filter((itm) => itm.label !== sillStyle)
                          .map((state) => (
                            <div
                              key={state.id}
                              onClick={() => handleClickCombo(state.label)}
                            >
                              <Choices
                                label={state.label}
                                variant={
                                  state.label === comboWindowSillStyle ||
                                  (state.label === "Raised Sill" &&
                                    comboWindowSillStyle?.includes("Dam"))
                                    ? "filled"
                                    : "outlined"
                                }
                                checkMark={
                                  state.label === comboWindowSillStyle ||
                                  (state.label === "Raised Sill" &&
                                    comboWindowSillStyle?.includes("Dam"))
                                    ? true
                                    : false
                                }
                                disabled={state.disabled || false}
                              />
                            </div>
                          ))}
                      </RowLayout>

                      {showRaisedComboWindow && (
                        <RowLayout>
                          {screenVal ? (
                            <Choices
                              label={raisedSills[1].label}
                              variant={"filled"}
                              checkMark={true}
                              disabled={true}
                            />
                          ) : (
                            raisedSills
                              .filter((itm) => itm.label !== sillStyle)
                              .map((state) => (
                                <div
                                  key={state.id}
                                  onClick={() => handleClickCombo(state.label)}
                                >
                                  <Choices
                                    label={state.label}
                                    variant={
                                      state.label === comboWindowSillStyle
                                        ? "filled"
                                        : "outlined"
                                    }
                                    checkMark={
                                      state.label === comboWindowSillStyle
                                    }
                                    disabled={
                                      !screenVal ? false : state.disabled
                                    }
                                  />
                                </div>
                              ))
                          )}
                        </RowLayout>
                      )}

                      {comboWindowSillStyle &&
                        comboWindowSillStyle !== sillStyle &&
                        sillImgs
                          .filter(
                            (image) => image.label === comboWindowSillStyle,
                          )
                          .map((imageShown) => (
                            <RowLayout key={imageShown.label}>
                              <Image
                                cloudName="ag-millworks"
                                secure="true"
                                publicId={
                                  screenVal && !isCentor
                                    ? imageShown.imgLabelScreen
                                    : screenVal && isCentor
                                      ? imageShown.imgLabelCentor
                                      : imageShown.imgLabel
                                }
                              />

                              {comboWindowSillStyle?.includes("Ultra") &&
                                !sillStyle?.includes("Ultra") && (
                                  <div style={{ marginLeft: 20 }}>
                                    <Image
                                      cloudName="ag-millworks"
                                      secure="true"
                                      publicId="Sills/bifolds/ultra-guide-weep"
                                    />
                                  </div>
                                )}
                            </RowLayout>
                          ))}
                    </ColLayoutSpacing>
                  )}
                </>
              )}
            </ColLayout>
          </>
        )}
      </ReusableAccordian>
    </>
  );
};

interface SillColorProps {
  sillColorChoice: string;
  handleClick: (lbl: string, type: string) => void;
}

export const SillColor: FC<SillColorProps> = ({
  sillColorChoice,
  handleClick,
}) => {
  return (
    <ColLayoutLeftDivider>
      <Typography style={{ margin: "auto" }} variant="subtitle1">
        Sill Colors
        <span style={{ color: "red", fontSize: 20 }}>*</span>
      </Typography>
      <RowLayout>
        {sillColor.map((state) => (
          <div
            key={state.id}
            onClick={() => handleClick(state.label, state.type)}
          >
            <Choices
              label={state.label}
              variant={state.label === sillColorChoice ? "filled" : "outlined"}
              checkMark={state.label === sillColorChoice}
              disabled={state.disabled || false}
            />
          </div>
        ))}
      </RowLayout>
    </ColLayoutLeftDivider>
  );
};

export default memo(SillAccordian);
